// SPDX-License-Identifier: AGPL-3.0-or-later

export interface ElementHost {
	set(next: HTMLElement | null, from?: HTMLElement | null): void;
	get(): HTMLElement | null;
	subscribe(listener: () => void): () => void;
	setCssVar(name: string, heightPx: number | null): void;
}

/**
 * Module-level portal host: one live element, subscribers, and a stale-unmount `from` guard.
 * The chat layout is instantiated in several views against one global host per overlay kind.
 */
export function createElementHost(): ElementHost {
	let host: HTMLElement | null = null;
	const listeners = new Set<() => void>();

	function notify(): void {
		for (const listener of listeners) listener();
	}

	function set(next: HTMLElement | null, from?: HTMLElement | null): void {
		if (next == null) {
			if (from != null && host !== from) return;
			if (host == null) return;
			host = null;
			notify();
			return;
		}
		if (host === next) return;
		host = next;
		notify();
	}

	function get(): HTMLElement | null {
		return host;
	}

	function subscribe(listener: () => void): () => void {
		listeners.add(listener);
		return () => {
			listeners.delete(listener);
		};
	}

	function setCssVar(name: string, heightPx: number | null): void {
		if (host == null) return;
		if (heightPx == null || heightPx <= 0) {
			host.style.removeProperty(name);
			return;
		}
		host.style.setProperty(name, `${heightPx}px`);
	}

	return {set, get, subscribe, setCssVar};
}
